import { z } from "zod"
import { commentSnippet } from "../comments/comments"
import { EventKind } from "./event-kind"
import { eventPayloads } from "./event-registry"

const recordColumns = {
    id: z.guid(),
    org_id: z.guid(),
    project_id: z.guid().nullable(),
    recipient_id: z.guid(),
    created_by: z.guid(),
    created_at: z.string(),
    read_at: z.string().nullable(),
    comment_text: z.string().nullish(),
}

const domainColumns = {
    id: z.guid(),
    orgId: z.guid(),
    projectId: z.guid().nullable(),
    recipientId: z.guid(),
    createdBy: z.guid(),
    createdAt: z.string(),
    readAt: z.string().nullable(),
    commentSnippet: z.string().optional(),
}

const member = <Columns extends z.ZodRawShape, K extends EventKind>(columns: Columns, kind: K) =>
    z.object({ ...columns, kind: z.literal(kind), data: eventPayloads[kind] })

const membersWith = <Columns extends z.ZodRawShape>(columns: Columns) =>
    Object.values(EventKind).map((kind) => member(columns, kind)) as {
        [K in EventKind]: ReturnType<typeof member<Columns, K>>
    }[EventKind][]

const asTuple = <T>(members: T[]) => members as [T, ...T[]]

export const notificationRecordSchema = z.discriminatedUnion(
    "kind",
    asTuple(membersWith(recordColumns))
)
export const notificationSchema = z.discriminatedUnion("kind", asTuple(membersWith(domainColumns)))

export type NotificationRecord = z.infer<typeof notificationRecordSchema>
export type Notification = z.infer<typeof notificationSchema>

export const notificationRecordToDomain = notificationRecordSchema.transform(
    (r) =>
        ({
            id: r.id,
            orgId: r.org_id,
            projectId: r.project_id,
            recipientId: r.recipient_id,
            createdBy: r.created_by,
            createdAt: r.created_at,
            readAt: r.read_at,
            kind: r.kind,
            data: r.data,
            commentSnippet: r.comment_text ? commentSnippet(r.comment_text) : undefined,
        }) as Notification
)
